/**
 * Client-side survey finish-gate (#2688).
 *
 * The survey requirement is driven purely by an attached survey — there is no
 * separate "requires_survey" flag (unlike photos/documents). So a project with
 * an active survey attached always requires it.
 *
 * Previously the Finish handler only blocked when `surveyQuestions.length > 0`,
 * which meant a survey that FAILED to load (network/server error) silently
 * un-gated checkout — the rep could finish the task skipping a survey the
 * project actually requires, with no warning (diag #59 class). This gate closes
 * that hole:
 *
 *  - load error  -> BLOCK (we don't know if a required survey exists; force retry)
 *  - mandatory Qs unanswered -> BLOCK
 *  - genuinely no/empty survey (loaded, zero questions) -> ALLOW (nothing to answer)
 *
 * Pure + dependency-free so the Finish handler can call it and it can be
 * unit-tested in isolation.
 */

export interface SurveyGateQuestion {
  qid: number;
  /** Undefined is treated as non-mandatory. */
  mandatory?: boolean;
}

export interface SurveyGateResponse {
  qid: number;
  responseText?: string;
}

export interface SurveyGateInput {
  /** True when the project's survey failed to load (network/server error). */
  surveyLoadError: boolean;
  /** Loaded survey questions ([] when none loaded / project has no survey). */
  questions: SurveyGateQuestion[];
  /** The rep's current responses. */
  responses: SurveyGateResponse[];
}

export type SurveyGateResult =
  | { blocked: false }
  | { blocked: true; kind: 'load-error' }
  | { blocked: true; kind: 'mandatory-unanswered'; remaining: number };

export function evaluateSurveyGate(input: SurveyGateInput): SurveyGateResult {
  // A survey that failed to load must never silently un-gate checkout.
  if (input.surveyLoadError) {
    return { blocked: true, kind: 'load-error' };
  }

  const remaining = input.questions
    .filter((q) => q.mandatory)
    .filter((q) => {
      const r = input.responses.find((x) => x.qid === q.qid);
      return !r || (r.responseText ?? '').trim().length === 0;
    }).length;

  if (remaining > 0) {
    return { blocked: true, kind: 'mandatory-unanswered', remaining };
  }

  return { blocked: false };
}
